// Engine-neutral concurrency cap for schema-introspection queries
// (SLICES.md § S7 item 7 — "pool contention").
//
// Before S7 a snapshot build fired eight introspection queries in one
// `Promise.all`. On a pool sized for interactive work that starves the user's
// OWN query: they press Execute while IntelliSense is warming and wait behind
// the catalog scans. Paging made it worse in raw count (dozens of page
// queries), so the pages are gated instead: at most
// `INTROSPECTION_MAX_CONCURRENCY` introspection queries per connection are in
// flight at any time, and everything else queues FIFO.
//
// Keyed by connection (not by connection+database): the pool is per
// connection, so that is the resource actually being shared. Engine-neutral so
// the per-engine caches of S7 item 8 reuse it.

/** Introspection queries allowed in flight per connection. */
export const INTROSPECTION_MAX_CONCURRENCY = 2;

type Lane = {
  active: number;
  /** FIFO queue of waiters; each resolves into a slot. */
  readonly waiting: (() => void)[];
};

const lanes = new Map<string, Lane>();

function laneFor(key: string): Lane {
  const existing = lanes.get(key);
  if (existing) return existing;
  const lane: Lane = { active: 0, waiting: [] };
  lanes.set(key, lane);
  return lane;
}

function release(key: string): void {
  const lane = lanes.get(key);
  if (!lane) return;
  const next = lane.waiting.shift();
  if (next) {
    // Hand the slot straight to the next waiter — `active` stays as-is.
    next();
    return;
  }
  lane.active -= 1;
  if (lane.active <= 0 && lane.waiting.length === 0) lanes.delete(key);
}

/**
 * Run `task` once a slot on `key`'s introspection lane is free. The slot is
 * released whether the task resolves or rejects, so one failed page query can
 * never wedge the lane.
 */
export async function runIntrospectionQuery<T>(key: string, task: () => Promise<T>): Promise<T> {
  const lane = laneFor(key);
  if (lane.active >= INTROSPECTION_MAX_CONCURRENCY) {
    await new Promise<void>((resolve) => {
      lane.waiting.push(resolve);
    });
  } else {
    lane.active += 1;
  }
  try {
    return await task();
  } finally {
    release(key);
  }
}

/** Introspection queries currently in flight for `key` (tests + diagnostics). */
export function introspectionQueriesInFlight(key: string): number {
  return lanes.get(key)?.active ?? 0;
}

/** Test-only: drop every lane between cases. */
export function __resetIntrospectionSemaphoreForTest(): void {
  lanes.clear();
}
